import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, Paper, Divider, TextField, List, ListItem, ListItemText, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Rating, Tooltip, CircularProgress, Alert, Avatar } from '@mui/material';
import { 
  Delete, 
  PlaylistAdd, 
  Feedback, 
  CheckCircle, 
  Cancel, 
  Download, 
  Science, 
  Chat,
  Add,
  Send,
  Assessment,
  TaskAlt,
  Star
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { collection, addDoc, deleteDoc, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';

interface FeedbackEntry {
  id: string;
  text: string;
  rating: number;
  userId?: string;
  userName?: string;
}

interface TestTask {
  id: string;
  description: string;
  completed: boolean;
  feedback: FeedbackEntry[];
  userId?: string;
  userName?: string;
}

const TestingPhase: React.FC = () => {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // Test tasks
  const [tasks, setTasks] = useState<TestTask[]>([]);
  const [newTask, setNewTask] = useState('');
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);

  // Feedback modal
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackTaskId, setFeedbackTaskId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  // General comments
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Firestore listeners
  useEffect(() => {
    if (!teamId || !sprintId) return;
    // Tasks
    const unsubTasks = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/testing_tasks`), snap => {
      setTasks(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as TestTask)));
    });
    // Comments
    const unsubComments = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/testing_comments`), snap => {
      setComments(snap.docs.map(doc => doc.data() as any));
    });
    return () => {
      unsubTasks();
      unsubComments();
    };
  }, [teamId, sprintId]);

  // Add test task
  const handleAddTask = async () => {
    if (newTask.trim() && teamId && sprintId) {
      setTaskLoading(true);
      setTaskError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/testing_tasks`), {
          description: newTask.trim(),
          completed: false,
          feedback: [],
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewTask('');
      } catch (err) {
        setTaskError('Failed to add task.');
      }
      setTaskLoading(false);
    }
  };

  // Toggle task completion
  const handleToggleComplete = async (id: string, completed: boolean) => {
    if (!teamId || !sprintId) return;
    setTaskLoading(true);
    setTaskError(null);
    try {
      await updateDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/testing_tasks`, id), {
        completed: !completed
      });
    } catch (err) {
      setTaskError('Failed to update task.');
    }
    setTaskLoading(false);
  };

  // Delete task
  const handleDeleteTask = async (id: string) => {
    if (!teamId || !sprintId) return;
    try {
      await deleteDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/testing_tasks`, id));
    } catch (err) {
      setTaskError('Failed to delete task.');
    }
  };

  // Open feedback modal
  const handleOpenFeedback = (taskId: string) => {
    setFeedbackTaskId(taskId);
    setFeedbackOpen(true);
    setFeedbackText('');
    setFeedbackRating(null);
  };

  // Close feedback modal
  const handleCloseFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackTaskId(null);
    setFeedbackText('');
    setFeedbackRating(null);
  };

  // Submit feedback
  const handleSubmitFeedback = async () => {
    if (!feedbackTaskId || !feedbackText.trim() || feedbackRating === null || !teamId || !sprintId) return;
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const taskRef = doc(firestore, `teams/${teamId}/designSprints/${sprintId}/testing_tasks`, feedbackTaskId);
      const taskSnap = await getDoc(taskRef);
      if (taskSnap.exists()) {
        const taskData = taskSnap.data();
        const newFeedback = {
          id: Date.now().toString(),
          text: feedbackText.trim(),
          rating: feedbackRating,
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        };
        await updateDoc(taskRef, {
          feedback: [...(taskData.feedback || []), newFeedback]
        });
        setFeedbackText('');
        setFeedbackRating(null);
      }
    } catch (err) {
      setFeedbackError('Failed to submit feedback.');
    }
    setFeedbackLoading(false);
  };

  // Comments
  const handleAddComment = async () => {
    if (newComment.trim() && teamId && sprintId) {
      setCommentLoading(true);
      setCommentError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/testing_comments`), {
          text: newComment.trim(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewComment('');
      } catch (err) {
        setCommentError('Failed to add comment.');
      }
      setCommentLoading(false);
    }
  };

  // Export results
  const handleExport = () => {
    const csvContent = [
      ['Task', 'Status', 'Average Rating', 'Feedback Count'],
      ...tasks.map(task => [
        task.description,
        task.completed ? 'Completed' : 'Pending',
        task.feedback.length > 0 
          ? (task.feedback.reduce((sum, f) => sum + f.rating, 0) / task.feedback.length).toFixed(1)
          : 'N/A',
        task.feedback.length.toString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `testing_results_${sprintId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate summary stats
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.completed).length;
  const avgRating = tasks.length > 0 
    ? (tasks.reduce((sum, task) => {
        if (task.feedback.length > 0) {
          return sum + (task.feedback.reduce((fSum, f) => fSum + f.rating, 0) / task.feedback.length);
        }
        return sum;
      }, 0) / tasks.filter(t => t.feedback.length > 0).length).toFixed(1)
    : 'N/A';

  return (
    <div className="space-y-6">
      {/* Results Summary Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
            <Assessment className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Results Summary</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Testing progress and metrics</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
            <div className="text-2xl font-bold text-blue-600 font-['Poppins']">{totalTasks}</div>
            <div className="text-sm text-gray-600 font-['Inter']">Total Tasks</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
            <div className="text-2xl font-bold text-green-600 font-['Poppins']">{completedTasks}</div>
            <div className="text-sm text-gray-600 font-['Inter']">Completed Tasks</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
            <div className="text-2xl font-bold text-purple-600 font-['Poppins']">{avgRating}</div>
            <div className="text-sm text-gray-600 font-['Inter']">Average Rating</div>
          </div>
        </div>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          disabled={totalTasks === 0}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            borderColor: '#3b82f6',
            color: '#3b82f6',
            '&:hover': {
              borderColor: '#2563eb',
              backgroundColor: '#eff6ff'
            }
          }}
        >
          Export Results (CSV)
        </Button>
      </div>

      {/* Test Tasks Section */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
            <TaskAlt className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Test Tasks</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Manage and track testing activities</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Add Test Task"
            value={newTask}
            onChange={e => setNewTask(e.target.value)}
            size="small"
            fullWidth
            disabled={taskLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10b981'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10b981',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Describe the test task..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddTask}
            disabled={taskLoading || !newTask.trim()}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%) !important',
                color: '#ffffff !important'
              }
            }}
          >
            Add
          </Button>
        </div>

        {taskLoading && (
          <div className="flex items-center gap-2 text-green-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Processing task...</span>
          </div>
        )}
        
        {taskError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {taskError}
          </Alert>
        )}

        <div className="space-y-3">
          {tasks.map(task => (
            <div key={task.id} className={`bg-white rounded-xl p-4 border transition-all duration-200 ${
              task.completed 
                ? 'border-green-300 shadow-lg bg-gradient-to-r from-green-50 to-emerald-50' 
                : 'border-gray-200 hover:shadow-md'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-gray-900 font-['Inter'] font-medium">{task.description}</p>
                    {task.completed && (
                      <Chip 
                        icon={<CheckCircle />}
                        label="Completed" 
                        color="success" 
                        size="small"
                        sx={{ 
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        }}
                      />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 font-['Inter']">by {task.userName || 'Unknown'}</p>
                  {task.feedback.length > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-gray-600 font-['Inter']">
                        {(task.feedback.reduce((sum, f) => sum + f.rating, 0) / task.feedback.length).toFixed(1)} ({task.feedback.length} feedback)
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip title={task.completed ? 'Mark as Incomplete' : 'Mark as Complete'}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleComplete(task.id, task.completed)}
                      color={task.completed ? 'success' : 'default'}
                      disabled={taskLoading}
                      sx={{
                        ...(task.completed && {
                          color: '#10b981',
                          '&:hover': { backgroundColor: '#d1fae5' }
                        })
                      }}
                    >
                      {task.completed ? <CheckCircle /> : <Cancel />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add/View Feedback">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenFeedback(task.id)}
                      color="primary"
                      disabled={taskLoading}
                      sx={{
                        color: '#3b82f6',
                        '&:hover': { backgroundColor: '#dbeafe' }
                      }}
                    >
                      <Feedback />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Task">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTask(task.id)}
                      disabled={taskLoading}
                      sx={{
                        color: '#ef4444',
                        '&:hover': { backgroundColor: '#fef2f2' }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>

        {tasks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <TaskAlt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No test tasks created yet. Start by adding your first test task!</p>
          </div>
        )}
      </div>

      {/* Feedback Dialog */}
      <Dialog open={feedbackOpen} onClose={handleCloseFeedback} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          Feedback for Test Task
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <div>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                Your Feedback
              </Typography>
              <TextField
                label="Feedback"
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={3}
                sx={{ 
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontFamily: 'Inter, sans-serif'
                  }
                }}
                disabled={feedbackLoading}
                placeholder="Share your testing feedback..."
              />
            </div>
            <div>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                Rating
              </Typography>
              <Rating
                value={feedbackRating}
                onChange={(_, value) => setFeedbackRating(value)}
                max={5}
                sx={{ mt: 1 }}
                disabled={feedbackLoading}
              />
            </div>
            <Divider />
            <div>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, mb: 2 }}>
                All Feedback for this Task
              </Typography>
              <div className="space-y-3">
                {tasks.find(t => t.id === feedbackTaskId)?.feedback.map(f => (
                  <div key={f.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'blue.500', fontSize: '0.75rem' }}>
                        {(f.userName || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <div className="flex-1">
                        <p className="text-gray-900 font-['Inter'] text-sm">{f.text}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Rating value={f.rating} readOnly size="small" max={5} />
                          <span className="text-xs text-gray-500 font-['Inter']">by {f.userName || 'Unknown'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {feedbackLoading && (
            <div className="flex items-center gap-2 text-blue-600 mt-4">
              <CircularProgress size={16} />
              <span className="text-sm font-['Inter']">Submitting feedback...</span>
            </div>
          )}
          {feedbackError && (
            <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif', mt: 2 }}>
              {feedbackError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseFeedback} 
            disabled={feedbackLoading}
            sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitFeedback} 
            variant="contained" 
            disabled={feedbackLoading || !feedbackText.trim() || feedbackRating === null}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '8px',
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%) !important',
                color: '#ffffff !important'
              }
            }}
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comments Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
            <Chat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Team Discussion</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share thoughts and collaborate</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Write a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            size="small"
            fullWidth
            disabled={commentLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Share your thoughts..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={commentLoading || !newComment.trim()}
            startIcon={<Send />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              }
            }}
          >
            Send
          </Button>
        </div>

        {commentLoading && (
          <div className="flex items-center gap-2 text-purple-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Sending comment...</span>
          </div>
        )}
        
        {commentError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {commentError}
          </Alert>
        )}

        <div className="space-y-3">
          {comments.map((comment, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'purple.500' }}>
                  {(comment.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{comment.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {comment.userName || 'Unknown'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {comments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Chat className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No comments yet. Start the discussion!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestingPhase; 